import { useState } from 'react'
import { Home } from 'lucide-react'
import { Review, useFetchReviews } from '../data'
import { useReviewsConfig } from '../providers'
import { ReviewStars } from './partials/review-stars'
import { ReviewAttribution } from './partials/review-attribution'
import { displayTitle, filterReviews } from '../helpers/utils'
import { Dialog, DialogHeader, DialogTitle } from '../../../components/ui/dialog'
import ResponsiveDialogContent from '../../../components/ResponsiveDialogContent'
import { useReviewsCss } from '../hooks/useReviewsCss'
import { ReviewPropertyLink } from './partials/review-property-link'

/**
 * Stacked-list layout for the reviews widget.
 *
 * The page-level list shows up to `initialCount` reviews. Clicking
 * "Show More" opens a modal containing the full list, with a "Load More"
 * button that fetches the next page on demand — matching the in-dialog
 * load-more of the grid/carousel/badge layouts.
 *
 * Layout intentionally ignores `popupForDetail` — this layout is the entire
 * reviews experience on its own.
 */
export function ReviewsList() {
  const options = useReviewsConfig()
  const listOptions = options.list

  const initialCount = listOptions?.initialCount ?? 3
  const showTitle = listOptions?.showTitle ?? true
  const showDividers = listOptions?.showDividers ?? true
  const showMoreText = listOptions?.showMoreText ?? 'Show More'
  const showMoreAlignment = listOptions?.showMoreAlignment ?? 'center'
  const modalTitle = listOptions?.modalTitle ?? 'All Reviews'

  const { data, isLoading } = useFetchReviews(options.filter)

  const allFetched: Review[] = data?.pages?.flatMap((p) => p.result || []) ?? []
  const totalItems = data?.pages?.[0]?.pagination?.total_items ?? allFetched.length
  const searching = Boolean(options.showSearch) && options.searchQuery.trim() !== ''
  const matched = filterReviews(allFetched, options.searchQuery)
  const visible = matched.slice(0, initialCount)

  const [isModalOpen, setIsModalOpen] = useState(false)

  if (isLoading || allFetched.length === 0) return null

  const canShowMore = (searching ? matched.length : totalItems) > initialCount

  return (
    <div className="homerunner-reviews-list w-full">
      <ReviewItemList
        reviews={visible}
        showTitle={showTitle}
        showDividers={showDividers}
        hideRating={Boolean(options.hideRating)}
        hideFeedback={Boolean(options.hideFeedback)}
      />

      {canShowMore && (
        <div
          className={`homerunner-reviews-list-actions homerunner-reviews-list-actions--${showMoreAlignment}`}
        >
          <button
            type="button"
            className="homerunner-reviews-list-show-more bg-accent"
            onClick={() => setIsModalOpen(true)}
          >
            {showMoreText}
          </button>
        </div>
      )}

      <ReviewsListModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        title={modalTitle}
        showTitle={showTitle}
        showDividers={showDividers}
      />
    </div>
  )
}

/**
 * Renders a stack of review rows. Used both on the page and inside the modal.
 */
function ReviewItemList({
  reviews,
  showTitle,
  showDividers,
  hideRating,
  hideFeedback,
}: {
  reviews: Review[]
  showTitle: boolean
  showDividers: boolean
  hideRating: boolean
  hideFeedback: boolean
}) {
  const options = useReviewsConfig()
  // #1692: list rows never showed the property, so with linkToProperty on
  // each row gains a "Stayed at <property>" footer carrying the link (plain
  // text when the feed has no slug for it); with the property hidden the
  // title carries the link instead. Off (the default) renders exactly as before.
  const linkProperty = Boolean(options.linkToProperty)
  return (
    <ul className="homerunner-reviews-list-items">
      {reviews.map((review, index) => {
        const isLast = index === reviews.length - 1
        const title = showTitle ? displayTitle(review) : null
        const showProperty = linkProperty && options.showPropertyName && Boolean(review.property_name)
        const linkTitle = linkProperty && !showProperty
        return (
          <li
            key={review.id}
            className={`homerunner-reviews-list-item${
              showDividers && !isLast ? ' homerunner-reviews-list-item--divided' : ''
            }`}
          >
            <ReviewAttribution review={review} className="homerunner-reviews-list-attribution" />
            <div className="homerunner-reviews-list-body">
              {!hideRating && (
                <div className="homerunner-reviews-list-rating">
                  <ReviewStars rating={review.rating} starClass="w-5 h-5" />
                </div>
              )}
              {title && (
                <h3 className="homerunner-reviews-list-title">
                  {linkTitle ? <ReviewPropertyLink review={review} className="homerunner-review-title-link">{title}</ReviewPropertyLink> : title}
                </h3>
              )}
              {!hideFeedback && review.content && (
                <p className="homerunner-reviews-list-content">{review.content}</p>
              )}
              {showProperty && (
                <div className="homerunner-reviews-list-property">
                  <Home className="homerunner-reviews-list-property-icon" aria-hidden="true" />
                  <span className="homerunner-reviews-list-property-label">
                    Stayed at{' '}
                    <ReviewPropertyLink review={review} className="homerunner-review-property-link">{review.property_name}</ReviewPropertyLink>
                  </span>
                </div>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function ReviewsListModal({
  open,
  onOpenChange,
  title,
  showTitle,
  showDividers,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  showTitle: boolean
  showDividers: boolean
}) {
  const options = useReviewsConfig()
  const cssString = useReviewsCss()

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    useFetchReviews(options.filter)

  const loaded: Review[] = data?.pages?.flatMap((p) => p.result || []) ?? []
  const allReviews = filterReviews(loaded, options.searchQuery)
  const totalItems = data?.pages?.[0]?.pagination?.total_items ?? loaded.length
  const averageRating = data?.pages?.[0]?.pagination?.average_rating

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent
        className={`homerunner-all-reviews-modal homerunner-reviews-list-modal h-[90vh] bg-background border border-border shadow-lg ${
          options.resolvedTheme === 'dark' ? 'dark' : ''
        }`}
        elevateDom
        cssString={cssString}
        style={{
          '--content-max-width': '900px',
        } as React.CSSProperties}
      >
        <DialogHeader className="flex-shrink-0 pb-4 sm:pb-6 px-0">
          <div className="flex flex-col items-center sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3">
            <DialogTitle className="text-base sm:text-2xl font-bold text-foreground text-center sm:text-left">
              {title} ({totalItems.toLocaleString()})
            </DialogTitle>
            {averageRating && !options.hideOverallRating && (
              <div className="flex items-center gap-2 sm:gap-3 text-muted-foreground sm:ml-auto">
                <span className="text-base sm:text-2xl font-semibold text-foreground">
                  {averageRating.toFixed(1)}
                </span>
                <ReviewStars rating={averageRating} starClass="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 px-0 homerunner-reviews-list-modal-scroll">
          {isLoading ? (
            <div className="homerunner-reviews-list-modal-loading">Loading…</div>
          ) : (
            <>
              <ReviewItemList
                reviews={allReviews}
                showTitle={showTitle}
                showDividers={showDividers}
                hideRating={Boolean(options.hideRating)}
                hideFeedback={Boolean(options.hideFeedback)}
              />

              {/* Load More button — matches the grid/carousel/badge dialog.
                  Styling comes from reviews.css (cloned into the dialog
                  portal) + `bg-accent`; Tailwind utilities don't reach the
                  elevated dialog shadow root. */}
              {hasNextPage && (
                <div className="homerunner-reviews-list-modal-load-more">
                  <button
                    type="button"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="homerunner-reviews-grid-load-more-button bg-accent"
                  >
                    {isFetchingNextPage ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  )
}
